// User-editable input bindings, persisted in localStorage.
//
// Couch co-op runs N emulator tiles in one page, each driven by a
// different input device. So bindings are no longer a single global
// map — they're an array of *player profiles*, one per tile slot.
// Profile p owns tile slot p (Player p+1).
//
// Each profile holds:
//   keyboard  — KeyboardEvent.code -> NES button
//   gamepad   — Web-Gamepad button index -> NES button
//   padIndex  — which navigator.getGamepads() slot drives this player
//               (null = no pad assigned; the InputHub auto-assigns a
//               freshly-connected pad to the lowest player without one)
//
// A given keyboard code belongs to *exactly one* (player, button) pair
// so the central InputHub can demux a keypress unambiguously — binding
// a code to one player drops it from every other player. Gamepad
// button maps are per-profile (each player has its own physical pad),
// so the same pad index can mean different things for different
// players without conflict.
//
// NES button numbering: A=0 B=1 Select=2 Start=3 Up=4 Down=5 Left=6 Right=7.
//
// Stick-axis -> D-pad bindings are not user-editable (they live in
// input.ts as a fixed deadzone mapping, applied to whichever pad a
// player owns).

export const NES_BUTTON_NAMES: readonly string[] = [
  "A", "B", "Select", "Start", "Up", "Down", "Left", "Right",
];

// Player 1: arrow keys + Z/X, the historical single-player default.
const DEFAULT_KEYBOARD_P1: Record<string, number> = {
  ArrowUp: 4, ArrowDown: 5, ArrowLeft: 6, ArrowRight: 7,
  KeyZ: 0, KeyX: 1,
  Enter: 3, ShiftLeft: 2, ShiftRight: 2,
};

// Player 2: the left-hand WASD cluster, so two people can share one
// keyboard out of the box (the example from the feature spec).
const DEFAULT_KEYBOARD_P2: Record<string, number> = {
  KeyW: 4, KeyS: 5, KeyA: 6, KeyD: 7,
  KeyF: 0, KeyG: 1,
  KeyV: 3, KeyC: 2,
};

const DEFAULT_GAMEPAD: Record<number, number> = {
  0: 0,   // bottom face -> NES A
  1: 1,   // right face  -> NES B
  2: 1,   // left face   -> NES B (alt)
  3: 0,   // top face    -> NES A (alt)
  8: 2,   // Select / Back
  9: 3,   // Start
  12: 4, 13: 5, 14: 6, 15: 7, // D-pad
};

export interface PlayerProfile {
  keyboard: Record<string, number>;
  gamepad: Record<number, number>;
  // Which navigator.getGamepads() slot drives this player, or null when
  // no pad is assigned. Multiple players never share a slot.
  padIndex: number | null;
}

// v1 was a single { keyboard, gamepad } object. v2 is an array of
// profiles. The storage key is bumped so a v1 blob is ignored rather
// than mis-parsed (the old single-map binding falls back to defaults —
// a one-time reset, acceptable for a dev-stage player).
const STORAGE_KEY = "bindings:v2";

function defaultProfile(playerIndex: number): PlayerProfile {
  const keyboard =
    playerIndex === 0 ? { ...DEFAULT_KEYBOARD_P1 }
    : playerIndex === 1 ? { ...DEFAULT_KEYBOARD_P2 }
    : {}; // P3+ expect a gamepad; no keyboard cluster left to hand out
  return { keyboard, gamepad: { ...DEFAULT_GAMEPAD }, padIndex: null };
}

let profiles: PlayerProfile[] = [defaultProfile(0)];

const listeners = new Set<() => void>();

function load(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return;
    const out: PlayerProfile[] = [];
    for (const entry of parsed) {
      if (!entry || typeof entry !== "object") continue;
      const e = entry as Partial<PlayerProfile>;
      const keyboard: Record<string, number> = {};
      if (e.keyboard && typeof e.keyboard === "object") {
        for (const k of Object.keys(e.keyboard)) {
          const v = (e.keyboard as Record<string, unknown>)[k];
          if (typeof v === "number") keyboard[k] = v;
        }
      }
      const gamepad: Record<number, number> = {};
      if (e.gamepad && typeof e.gamepad === "object") {
        for (const k of Object.keys(e.gamepad)) {
          const idx = Number(k);
          const v = (e.gamepad as Record<string, unknown>)[k];
          if (!Number.isNaN(idx) && typeof v === "number") gamepad[idx] = v;
        }
      }
      const padIndex = typeof e.padIndex === "number" ? e.padIndex : null;
      out.push({ keyboard, gamepad, padIndex });
    }
    if (out.length > 0) profiles = out;
  } catch {
    /* ignore corrupt saved data */
  }
}

function save(): void {
  try {
    // padIndex is a live hardware-slot assignment that isn't meaningful
    // across reloads (the pad may be gone), but persisting it is
    // harmless — the InputHub validates the slot is connected before
    // polling it, and re-assigns on the next gamepadconnected.
    const serializable = profiles.map(p => {
      const gamepadOut: Record<string, number> = {};
      for (const k of Object.keys(p.gamepad)) gamepadOut[k] = p.gamepad[Number(k)]!;
      return { keyboard: p.keyboard, gamepad: gamepadOut, padIndex: p.padIndex };
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    /* ignore quota errors */
  }
  for (const fn of listeners) fn();
}

load();

export function getProfiles(): readonly PlayerProfile[] {
  return profiles;
}

export function getProfileCount(): number {
  return profiles.length;
}

// Ensure at least `count` profiles exist (the tile manager calls this
// as tiles are added). Newly-created profiles get the per-index
// defaults. Never shrinks — removing a tile keeps its profile so a
// re-added tile recovers the user's customizations. Returns true if it
// grew (so callers can persist).
export function ensureProfiles(count: number): void {
  if (count <= profiles.length) return;
  while (profiles.length < count) profiles.push(defaultProfile(profiles.length));
  save();
}

export function getKeyboardMap(player: number): Readonly<Record<string, number>> {
  return profiles[player]?.keyboard ?? {};
}
export function getGamepadMap(player: number): Readonly<Record<number, number>> {
  return profiles[player]?.gamepad ?? {};
}
export function getPadIndex(player: number): number | null {
  return profiles[player]?.padIndex ?? null;
}

// Bind a keyboard code to a NES button for one player. A code maps to
// exactly one (player, button) pair, so we first strip it from every
// profile — including this one — before assigning. This keeps the
// InputHub's keycode demux unambiguous (no key drives two players).
export function addKeyboardBinding(player: number, nesBtn: number, code: string): void {
  const profile = profiles[player];
  if (!profile) return;
  for (const p of profiles) {
    if (code in p.keyboard) delete p.keyboard[code];
  }
  profile.keyboard[code] = nesBtn;
  save();
}

export function removeKeyboardBinding(player: number, code: string): void {
  const profile = profiles[player];
  if (!profile || !(code in profile.keyboard)) return;
  delete profile.keyboard[code];
  save();
}

export function addGamepadBinding(player: number, nesBtn: number, padBtn: number): void {
  const profile = profiles[player];
  if (!profile) return;
  if (padBtn in profile.gamepad) delete profile.gamepad[padBtn];
  profile.gamepad[padBtn] = nesBtn;
  save();
}

export function removeGamepadBinding(player: number, padBtn: number): void {
  const profile = profiles[player];
  if (!profile || !(padBtn in profile.gamepad)) return;
  delete profile.gamepad[padBtn];
  save();
}

// Assign a physical gamepad slot to a player (or null to unassign). A
// slot drives at most one player, so assigning it here removes it from
// any other player that held it. The InputHub also calls this to record
// its auto-assignment of a freshly-connected pad.
export function setPadIndex(player: number, padIndex: number | null): void {
  const profile = profiles[player];
  if (!profile) return;
  if (padIndex !== null) {
    for (const p of profiles) {
      if (p !== profile && p.padIndex === padIndex) p.padIndex = null;
    }
  }
  profile.padIndex = padIndex;
  save();
}

// Reset one player's profile back to its per-index defaults.
export function resetPlayerBindings(player: number): void {
  if (player < 0 || player >= profiles.length) return;
  profiles[player] = defaultProfile(player);
  save();
}

// Subscribe to binding changes. Returns an unsubscribe function.
export function onChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// Pretty-print a KeyboardEvent.code value for the UI.
export function formatKeyCode(code: string): string {
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code === "ArrowUp") return "↑";
  if (code === "ArrowDown") return "↓";
  if (code === "ArrowLeft") return "←";
  if (code === "ArrowRight") return "→";
  if (code === "ShiftLeft") return "Shift L";
  if (code === "ShiftRight") return "Shift R";
  if (code === "ControlLeft") return "Ctrl L";
  if (code === "ControlRight") return "Ctrl R";
  if (code === "AltLeft") return "Alt L";
  if (code === "AltRight") return "Alt R";
  if (code === "MetaLeft" || code === "OSLeft") return "Meta L";
  if (code === "MetaRight" || code === "OSRight") return "Meta R";
  if (code === "Space") return "Space";
  return code;
}

// Pretty-print a gamepad button index. Uses W3C "Standard Gamepad"
// names where applicable.
export function formatGamepadButton(idx: number): string {
  const names: Record<number, string> = {
    0: "Btn 0 (A)", 1: "Btn 1 (B)", 2: "Btn 2 (X)", 3: "Btn 3 (Y)",
    4: "L1", 5: "R1", 6: "L2", 7: "R2",
    8: "Select", 9: "Start",
    10: "L3", 11: "R3",
    12: "D-Up", 13: "D-Down", 14: "D-Left", 15: "D-Right",
    16: "Guide",
  };
  return names[idx] ?? `Btn ${idx}`;
}
